import { useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { displayToastError } from '@/helpers';
import { logger } from '@/utils/logger/logger';

import { completeOAuthCallback } from '../../settings/api/oauth-integrations.api';
import { useSynchronizeIntegration } from '../../settings/hooks/use-synchronize-integration';
import { clearOAuthState } from '../../settings/utils/oauth-state';
import { IntegrationServices } from '../types/integrations';

const TAG = 'useOAuthProcessor:';

interface UseOAuthProcessorParams {
  /** Callback when OAuth flow completes successfully */
  onSuccess?: (integration: IntegrationServices) => void;
  /** Callback when OAuth flow fails */
  onError?: (error: Error) => void;
}

/**
 * Hook that processes OAuth callback parameters from URL.
 * Handles POST request to backend and triggers integration synchronization.
 * Should be used on pages that are OAuth callback destinations (returnUrl).
 *
 * @example
 * ```tsx
 * useOAuthProcessor({
 *   onSuccess: (integration) => {
 *     console.log(`${integration} connected successfully`);
 *   }
 * });
 * ```
 */
export const useOAuthProcessor = ({ onSuccess, onError }: UseOAuthProcessorParams = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const synchronizeMutation = useSynchronizeIntegration();

  const callbackMutation = useMutation({
    mutationFn: completeOAuthCallback,
    onSuccess: async () => {
      // Get integration from URL params
      const integration = searchParams.get('integration') as IntegrationServices | null;

      if (!integration) {
        logger.error(TAG, 'Integration parameter missing from URL');
        displayToastError('Integration parameter is missing');
        clearOAuthState();
        return;
      }

      // Show success toast
      toast.success(`${integration} integration successfully connected`);

      // Trigger synchronization for the integration
      try {
        await synchronizeMutation.mutateAsync({ integrations: [integration] });
      } catch (error) {
        logger.error(TAG, 'Failed to synchronize after OAuth', error);
        // Continue anyway, user can manually refresh
      }

      // Clear OAuth state from localStorage
      clearOAuthState();

      // Remove OAuth params from URL
      searchParams.delete('integration');
      searchParams.delete('state');
      searchParams.delete('code');
      searchParams.delete('realmId');
      setSearchParams(searchParams, { replace: true });

      // Call success callback
      onSuccess?.(integration);
    },
    onError: (error: Error) => {
      logger.error(TAG, 'OAuth callback failed', error);
      displayToastError(error.message);

      // Clear OAuth state and URL params
      clearOAuthState();
      searchParams.delete('integration');
      searchParams.delete('state');
      searchParams.delete('code');
      searchParams.delete('realmId');
      setSearchParams(searchParams, { replace: true });

      // Call error callback
      onError?.(error);
    },
  });

  useEffect(() => {
    // Check if we have OAuth callback parameters
    const integration = searchParams.get('integration');
    const state = searchParams.get('state');
    const code = searchParams.get('code');
    const realmId = searchParams.get('realmId');

    if (!integration || !state || !code) {
      // No OAuth parameters, nothing to process
      return;
    }

    logger.debug(TAG, 'Processing OAuth callback', {
      integration,
      hasRealmId: !!realmId,
    });

    // Complete OAuth callback
    callbackMutation.mutate({
      state,
      code,
      ...(realmId && { realmId }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount
};
